import * as React from 'react';
import styled from 'styled-components';
import theme from '../../../util/theme';
import { laptopSize, mobileSize } from '../../../util/variables';
import axios from 'axios';

const DownloadReportForm = () => {
  const [formData, setFormData] = React.useState({
    firstName: '',
    lastName: '',
    title: '',
    company: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Submit form data to Netlify function
      await axios.post('/.netlify/functions/submit-download-report-form', formData);

      // Trigger PDF download
      const pdfUrl = '/pdf/Base10-2025-Purpose-Report.pdf';
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.target = '_blank';
      link.click();

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        title: '',
        company: '',
        email: '',
      });
    } catch (err: any) {
      console.error('Error submitting form:', err);
      setError(
        err.response?.data?.error || 'There was an error submitting the form. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Container>
      <Wrapper>
        <FormHeader>
          <Title>Download our annual Purpose report.</Title>
          <Subtitle>
            For more information about our purpose work, send us a message at{' '}
            <EmailLink href="mailto:purpose@base10.vc">purpose@base10.vc</EmailLink>
          </Subtitle>
        </FormHeader>
        <FormContainer>
          <Form onSubmit={handleSubmit}>
            <Input
              required
              disabled={isSubmitting}
              name="firstName"
              placeholder="First Name"
              type="text"
              value={formData.firstName}
              onChange={handleChange}
            />
            <Input
              required
              disabled={isSubmitting}
              name="lastName"
              placeholder="Last Name"
              type="text"
              value={formData.lastName}
              onChange={handleChange}
            />
            <Input
              required
              disabled={isSubmitting}
              name="title"
              placeholder="Title"
              type="text"
              value={formData.title}
              onChange={handleChange}
            />
            <Input
              required
              disabled={isSubmitting}
              name="company"
              placeholder="Company"
              type="text"
              value={formData.company}
              onChange={handleChange}
            />
            <Input
              required
              disabled={isSubmitting}
              name="email"
              placeholder="Email"
              type="email"
              value={formData.email}
              onChange={handleChange}
            />
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <DownloadButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Loading...' : 'Download'}
            </DownloadButton>
          </Form>
        </FormContainer>
      </Wrapper>
    </Container>
  );
};

const Container = styled.section`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${theme.BLACK_COLOR};
`;

const Wrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  padding: 140px 44px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 84px;

  @media ${laptopSize} {
    flex-direction: column;
    gap: 40px;
    padding: 60px 44px 110px 44px;
  }
`;

const FormHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h2`
  font-size: 36px;
  line-height: 1.2;
  font-weight: bold;
  width: 18ch;
  color: ${theme.WHITE_COLOR};
  text-align: left;
  margin: 0;
  @media ${laptopSize} {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  width: 36ch;
  color: ${theme.WHITE_COLOR};
  text-align: left;
  line-height: 1.4;
  margin: 0;
  @media ${laptopSize} {
    width: 26ch;
  }
`;

const EmailLink = styled.a`
  color: ${theme.WHITE_COLOR};
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: ${theme.PRIMARY_COLOR_DARK};
  }
  &:visited {
    color: ${theme.WHITE_COLOR};
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 730px;
  background-color: ${theme.WHITE_COLOR};
  padding: 40px;
  @media (max-width: ${mobileSize}px) {
    padding: 20px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: end;
  gap: 20px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 0;
  border: none;
  border-radius: 0px;
  border-bottom: 1px solid ${theme.BLACK_COLOR};
  font-size: 18px;
  background: transparent;
  &::placeholder {
    color: ${theme.BLACK_COLOR};
  }
  &:focus {
    outline: none;
    border-bottom-width: 2px;
    border-bottom: 1px solid ${theme.PRIMARY_COLOR_DARK};
  }
`;

const DownloadButton = styled.button`
  width: 50%;
  height: 38px;
  background-color: ${theme.PRIMARY_COLOR_DARK};
  color: ${theme.WHITE_COLOR};
  border: none;
  border-radius: 5px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 20px;
  &:hover {
    opacity: 0.9;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 8px;
  width: 100%;
  text-align: left;
`;

export default DownloadReportForm;
